import { React, useState, useEffect } from 'react';
import DeleteIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import { useDispatch } from 'react-redux'; 
import { fetchRemoveNews } from '../../store/reducers/news';
import { Box, Typography, IconButton, CardMedia, Button } from "@mui/material";
import dayjs from 'dayjs';
import { baseUrlApi } from '../../components/constants';
import ImageGallery from '../imageGalery/ImageGalery';

const MIN_SYMBOL_COUNT = 300; 

// Функция для преобразования URL в гиперссылки в тексте новости
const convertUrlsToLinks = (text) => {
    if (!text) return '';
    
    // Защищаем уже существующие теги <a> от обработки
    const preservedTags = [];
    let processedText = text.replace(/<a\s+[^>]*>.*?<\/a>/gi, (match) => {
        preservedTags.push(match);
        return `__PRESERVE_TAG_${preservedTags.length - 1}__`;
    });
    
    // Регулярка для ссылок: http://, https://, и домены без протокола
    // Сопоставляет: site.ru, www.site.ru, sub.domain.ru, domain.ru/path, domain.ru?query, domain.ru#hash
    const urlRegex = /(?:https?:\/\/)?(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)/gi;
    
    processedText = processedText.replace(urlRegex, (url) => {
        // Проверяем, не является ли URL частью защищенного тега (остатки маркеров)
        if (url.includes('__PRESERVE_TAG_')) return url;
        
        let cleanUrl = url;
        let trailingChar = '';
        
        // Убираем знаки препинания в конце
        if (cleanUrl.endsWith('.') || cleanUrl.endsWith(',') || cleanUrl.endsWith(';') || cleanUrl.endsWith('!') || cleanUrl.endsWith('?')) {
            trailingChar = cleanUrl.slice(-1);
            cleanUrl = cleanUrl.slice(0, -1);
        }
        
        // Добавляем http:// если нет протокола
        let fullUrl = cleanUrl;
        if (!fullUrl.startsWith('http://') && !fullUrl.startsWith('https://')) {
            fullUrl = 'http://' + fullUrl;
        }
        
        // Формируем отображаемый текст (без http:// и www.)
        let displayText = cleanUrl;
        try {
            const urlObj = new URL(fullUrl);
            displayText = urlObj.hostname.replace(/^www\./, '');
            if (urlObj.pathname !== '/') {
                displayText += urlObj.pathname;
            }
        } catch (e) {
            displayText = cleanUrl;
        }
        
        return `<a href="${fullUrl}" target="_blank" rel="noopener noreferrer" style="color: #1976d2; text-decoration: underline;">${displayText}</a>${trailingChar}`;
    });
    
    // Возвращаем защищённые теги <a> обратно
    processedText = processedText.replace(/__PRESERVE_TAG_(\d+)__/g, (match, index) => {
        return preservedTags[parseInt(index)];
    });
    
    return processedText;
};

// Функция для очистки текста от пустых параграфов
const cleanEmptyParagraphs = (html) => {
    if (!html) return '';
    let cleaned = html.replace(/<p>\s*<\/p>/g, '');
    cleaned = cleaned.replace(/<p>&nbsp;<\/p>/g, '');
    return cleaned;
};

export const CardNews = ({
  id,
  title,
  imageUrl,
  imagesUrls,
  isFullPost,
  isLoading,
  isEditable,
  textNews,
  dateNewsFormat,
  linkProgramm,
  linkNews,
  programName,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLongText, setIsLongText] = useState(false);
  
  const getTextLength = (html) => {
    if (!html) return 0;
    const textWithoutTags = html.replace(/<[^>]*>/g, '');
    return textWithoutTags.length;
  };
  
  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const dispatch = useDispatch();
  const onClickRemove = () => {
    if (confirm("Вы действительно хотите удалить новость")) {
      dispatch(fetchRemoveNews(id));
    }
  };

  const getImagesArray = () => {
    if (imagesUrls && imagesUrls.length > 0) {
      return imagesUrls;
    }
    if (imageUrl && imageUrl !== '') {
      return [imageUrl];
    }
    return [];
  };
  
  const newsImages = getImagesArray();
  const hasMultipleImages = newsImages.length > 1;

  const dateNewsFormatString = dayjs(dateNewsFormat).format('MM.YYYY');
  
  const getDisplayHtml = () => {
    if (!textNews) return '';
    let html = cleanEmptyParagraphs(textNews);
    html = convertUrlsToLinks(html);
    return html;
  };
  
  useEffect(() => {
    if (textNews) {
      const textLength = getTextLength(textNews);
      setIsLongText(textLength > MIN_SYMBOL_COUNT);
    }
  }, [textNews]);
  
  return (
    <div style={{ marginBottom: 40, borderBottom: '1px solid #eee', paddingBottom: 20 }}>
      {isEditable && (
        <Box style={{ textAlign: "right" }}>
          <a href={`/news/${id}/edit`}>
            <IconButton color="primary">
              <EditIcon />
            </IconButton>  
          </a>
          <IconButton onClick={onClickRemove} color="secondary">
            <DeleteIcon />
          </IconButton>
        </Box>
      )}
      
      <Typography ml='0px' color="#000000" variant="h5" style={{ fontWeight: 'bold' }} gutterBottom>
        {title}
      </Typography>
      
      {newsImages.length > 0 && (
        hasMultipleImages ? (
          <ImageGallery 
            images={newsImages} 
            title={title}
            containerHeight={400}
          />
        ) : (
          <CardMedia
            component="img"
            alt={title}
            image={`${baseUrlApi}${newsImages[0]}`}
            sx={{ 
              width: '100%', 
              display: "block", 
              margin: "0 auto",
              borderRadius: 2,
              maxHeight: 400,
              objectFit: 'contain',
              mb: 2,
              cursor: 'pointer'
            }} 
          />
        )
      )}
      
      <div>
        <Typography 
          component="div"
          style={{ 
            fontSize: '20px', 
            textAlign: 'justify', 
            overflow: 'hidden', 
            textOverflow: 'ellipsis', 
            display: '-webkit-box', 
            WebkitLineClamp: isExpanded ? 'unset' : 8, 
            WebkitBoxOrient: 'vertical',
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            lineHeight: 1.5
          }}
        >
          <div dangerouslySetInnerHTML={{ __html: getDisplayHtml() }} />
          
          {programName && linkProgramm ? (
            <div style={{ marginTop: 15 }}>
              Занятия проводятся по образовательной программе:{" "}
              <a href={linkProgramm} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
                {programName}
              </a>
            </div>
          ) : programName ? (
            <div style={{ marginTop: 15 }}>
              Подготовка ведется по образовательной программе: <strong>{programName}</strong>
            </div>
          ) : null}
          
          {linkNews && (
            <div style={{ textAlign: "right", marginTop: 10 }}>
              <a href={linkNews} target='_blank' rel='noopener noreferrer' style={{ textDecoration: 'none', color: '#1976d2' }}>
                <b>Подробнее в источнике</b>
              </a>
            </div>
          )}
          
          {dateNewsFormat && (
            <div style={{ textAlign: 'right', marginTop: 10, color: '#666' }}>
              {dateNewsFormatString}
            </div>
          )}
        </Typography>
        
        {!isExpanded && isLongText && (
          <Box style={{ textAlign: "right", marginTop: 10 }}>
            <Button onClick={handleToggleExpand}>
              читать далее...
            </Button>
          </Box>
        )}
      </div>
    </div>
  );
};